import { scoreQueryOptions } from "../scores/scoreQueryOptions";
/**
 * ISSUE-987. Both teams' rosters, grouped by position, with starters first
 * when the game has a stored lineup. v1 reference: docs/features/game-detail/
 * v1-match-detail-reference.md section 3f. The roster is the team's season
 * squad (sports-api's /team/roster-get), not this game's lineup: starters
 * come from events-api's /event/lineups-get, which answers its documented
 * 404 for a game with no stored lineup, and that is never shown as an error.
 */
import { useEventLineupsGet } from "@sportsmet/events-sdk/private";
import { useTeamRosterGet } from "@sportsmet/sports-sdk/private";
import { SegmentedControl, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { PlayerHeadshot } from "../playerProfile/PlayerHeadshot";
import {
  buildLineupSections,
  filterRosterGroups,
  resolveStarterIds,
} from "./lineupTabLogic";
import { resolvePlatePair } from "./teamPlateText";
import { GAME_SECTION_CARD_CLASS } from "./GameSectionCard";

export type typeLineupTabTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
};

export type typeLineupTabSide = "away" | "home";

/**
 * Reports the chosen side up to the screen, which owns the URL
 * (`?lineupTeam=`, `routeState/sectionSearch.ts`) -- same split as
 * `features/notifications/NotificationFilterChips.tsx`. A component-owned
 * `useState` here would make browser back, a hard refresh and a shared link
 * all lose which roster the fan was reading.
 */
export const LineupTab = ({
  eventId,
  away,
  home,
  side,
  onSelectSide,
}: {
  eventId: string;
  away: typeLineupTabTeam;
  home: typeLineupTabTeam;
  side: typeLineupTabSide;
  onSelectSide: (payload: { side: typeLineupTabSide }) => void;
}): ReactNode => {
  const team = side === "away" ? away : home;

  const roster = useTeamRosterGet({ teamId: team.teamId });
  const lineups = useEventLineupsGet(
    { eventId },
    { ...scoreQueryOptions, refetchInterval: 120_000 },
  );

  // roster.data is deliberately left out of the dependency list: this effect
  // should fire once per new roster answer, keyed by roster.dataUpdatedAt and
  // the team currently shown, not on every render that holds the same data.
  useEffect(() => {
    if (extractErrorCode(roster.data) !== null) {
      showApiError(roster.data, undefined, roster.dataUpdatedAt);
    }
  }, [roster.dataUpdatedAt, team.teamId]);

  const rosterHasError = extractErrorCode(roster.data) !== null;
  const rosterData = roster.data?.data;
  // ISSUE-1086. The injured group is listed on the Injuries tab, not here, so
  // a roster whose only players are injured reads as empty.
  const shownGroups = filterRosterGroups(rosterData?.groups ?? []);
  const playerCount = shownGroups.reduce(
    (count, group) => count + group.players.length,
    0,
  );

  return (
    <div className="flex flex-col gap-4" data-testid="lineup-tab">
      <SegmentedControl
        label="Choose a team"
        fullWidth
        testId="lineup-team-switch"
        value={side}
        options={[
          { value: "away", label: resolvePlatePair({ away, home }).awayText },
          { value: "home", label: resolvePlatePair({ away, home }).homeText },
        ]}
        onValueChange={(next) => {
          onSelectSide({ side: next === "home" ? "home" : "away" });
        }}
      />

      {roster.isPending ? (
        <div data-testid="lineup-tab-loading">
          <Skeleton shape="card" />
        </div>
      ) : rosterHasError ? null : playerCount === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="lineup-tab-empty"
        >
          {rosterData?.emptyReason ??
            "This team's roster has not been published yet."}
        </p>
      ) : (
        buildLineupSections({
          groups: shownGroups,
          starterPlayerIds: resolveStarterIds({
            lineup: lineups.data?.data ?? null,
            teamId: team.teamId,
          }),
        }).map((section) => (
          <section
            key={section.groupKey}
            className={GAME_SECTION_CARD_CLASS + " gap-1 p-3"}
            data-testid="lineup-section"
          >
            <h4 className="text-body-strong text-ink">{section.label}</h4>
            <ul className="flex flex-col">
              {section.players.map((player) => (
                <li key={player.playerId}>
                  <Link
                    to="/scores/player"
                    search={{
                      playerId: player.playerId,
                      name: player.name,
                      teamId: team.teamId,
                      teamName: team.name,
                      teamAbbreviation: team.abbreviation,
                      jerseyNumber: player.jerseyNumber ?? undefined,
                      position: player.position ?? undefined,
                    }}
                    className="flex min-h-11 items-center gap-3"
                    data-testid="lineup-player-row"
                  >
                    <PlayerHeadshot
                      headshotUrl={player.headshotUrl}
                      size="md"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-body text-ink">{player.name}</span>
                      {player.jerseyNumber !== null ? (
                        <span className="text-caption text-ink-tertiary">
                          #{player.jerseyNumber}
                        </span>
                      ) : null}
                    </span>
                    {player.position !== null ? (
                      <span className="text-caption text-ink-secondary">
                        {player.position}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
};
